/**
 * Contest picks-core team image folder (from `style_config.teamAssetFolder`).
 *
 * Mirrors Admin `asset-folders.ts` / Pick'em frontend `leagueIp.ts`:
 * when set, team abbreviation → image URLs use that folder; otherwise League IP
 * defaults apply (`teams`/`helmet` when IP on, `jerseys`/`team-jerseys` when off).
 */

import { getFileExtForSport } from '@low6-pickem/sports-config';
import { normalizeMlbBlobTeamCode } from '@/utils/mlbBlobTeamCode';

const STORAGE_ACCOUNT = import.meta.env.VITE_BLOB_STORAGE_ACCOUNT_NAME || 'draftkingsdev';
const CONTAINER = import.meta.env.VITE_BLOB_STORAGE_CONTAINER || 'picks-core';
const CDN_BASE_URL = `https://${STORAGE_ACCOUNT}.blob.core.windows.net/${CONTAINER}`;

export type TeamAssetFolderId =
  | 'teams'
  | 'jerseys'
  | 'jerseys-position'
  | 'team-jerseys'
  | 'helmet';

/**
 * Positions `jerseys-position` actually has artwork for.
 *
 * The folder is keyed `{TEAM}_{POSITION}.png` (e.g. `nfl/jerseys-position/ARI_QB.png`) and
 * holds these four positions only. A kicker, a defence, or any defensive player who scores
 * has no file there, so the URL builder falls back to the plain team jersey.
 */
const JERSEY_POSITIONS = ['QB', 'RB', 'TE', 'WR'] as const;
type JerseyPosition = (typeof JERSEY_POSITIONS)[number];

/** The `jerseys-position` position for a roster position string, or null when unsupported. */
export function resolveJerseyPosition(position: string | null | undefined): JerseyPosition | null {
  if (!position) return null;
  // Roster values are occasionally compound ("WR/RB"); the first token is the primary
  // position and is what the artwork is keyed on.
  const primary = position
    .trim()
    .toUpperCase()
    .split(/[^A-Z]+/)
    .filter(Boolean)[0];
  if (!primary) return null;
  return (JERSEY_POSITIONS as readonly string[]).includes(primary)
    ? (primary as JerseyPosition)
    : null;
}

const FOLDERS_BY_SPORT: Record<string, TeamAssetFolderId[]> = {
  nfl: ['teams', 'jerseys', 'jerseys-position', 'helmet'],
  nba: ['teams', 'team-jerseys'],
  /*
   * WNBA has generic jerseys only. No `wnba/teams/` logo set has been supplied, so listing
   * `teams` here would let the League IP branch below resolve a folder that 404s. Add it
   * when licensed logos land.
   */
  wnba: ['team-jerseys'],
  mlb: ['teams', 'jerseys'],
  ncaamb: ['teams'],
  ncaafb: ['helmet', 'jerseys', 'teams'],
  nhl: ['teams'],
};

let teamAssetFolder: TeamAssetFolderId | null = null;
let leagueIpEnabled = false;

export function isTeamAssetFolderId(value: unknown): value is TeamAssetFolderId {
  return (
    value === 'teams' ||
    value === 'jerseys' ||
    value === 'jerseys-position' ||
    value === 'team-jerseys' ||
    value === 'helmet'
  );
}

/** Contest-level folder override from `style_config.teamAssetFolder`. */
export function setTeamAssetFolder(folder: string | null | undefined): void {
  teamAssetFolder = isTeamAssetFolderId(folder) ? folder : null;
}

export function setLeagueIp(enabled: boolean): void {
  leagueIpEnabled = enabled;
}

/**
 * Daily Reveal's house default per sport, used ahead of the generic non-IP jersey folder.
 *
 * Every Daily Reveal card shows a single player, so NFL wants the position-cut jersey rather
 * than the plain team one. Mirrors `DEFAULT_FOLDER_BY_GAME_TYPE.daily_reveal` in Admin
 * `asset-folders.ts`, which writes the same default into `style_config.teamAssetFolder`.
 * Only applied when League IP is off, since a licensed contest must still get licensed marks.
 */
const DAILY_REVEAL_DEFAULT_FOLDER: Record<string, TeamAssetFolderId> = {
  nfl: 'jerseys-position',
};

export function defaultTeamAssetFolder(sport: string, leagueIp: boolean): TeamAssetFolderId {
  const sportLower = sport.trim().toLowerCase();
  const available = FOLDERS_BY_SPORT[sportLower] ?? ['teams'];

  if (leagueIp) {
    if (sportLower === 'ncaafb' && available.includes('helmet')) return 'helmet';
    return available.includes('teams') ? 'teams' : available[0];
  }

  const gameDefault = DAILY_REVEAL_DEFAULT_FOLDER[sportLower];
  if (gameDefault && available.includes(gameDefault)) return gameDefault;

  if ((sportLower === 'nba' || sportLower === 'wnba') && available.includes('team-jerseys'))
    return 'team-jerseys';
  if (available.includes('jerseys')) return 'jerseys';
  return available[0] ?? 'teams';
}

/** Effective folder for the active contest (explicit override → League IP default). */
export function resolveEffectiveTeamAssetFolder(
  sport: string | null | undefined,
  leagueIp: boolean = leagueIpEnabled
): TeamAssetFolderId {
  const sportKey = sport?.trim() || 'MLB';
  if (teamAssetFolder) {
    const available = FOLDERS_BY_SPORT[sportKey.toLowerCase()] ?? ['teams'];
    if (available.includes(teamAssetFolder)) return teamAssetFolder;
  }
  return defaultTeamAssetFolder(sportKey, leagueIp);
}

function resolveBlobAbbr(teamAbbr: string, sport: string): string {
  const sportLower = sport.trim().toLowerCase();
  if (sportLower === 'mlb') {
    return normalizeMlbBlobTeamCode(teamAbbr) || teamAbbr.trim().toUpperCase();
  }
  return teamAbbr.trim().toUpperCase();
}

/**
 * Build a CDN URL for a team abbreviation in the selected asset folder.
 */
export function generateTeamAssetUrl(
  teamAbbr: string,
  sport: string,
  folder: TeamAssetFolderId,
  position?: string | null
): string {
  const sportLower = sport.trim().toLowerCase();
  const code = resolveBlobAbbr(teamAbbr, sportLower);
  if (!code) return '';

  switch (folder) {
    case 'jerseys':
      return `${CDN_BASE_URL}/${sportLower}/jerseys/${code}.png`;
    case 'jerseys-position': {
      const pos = resolveJerseyPosition(position);
      if (!pos) return `${CDN_BASE_URL}/${sportLower}/jerseys/${code}.png`;
      return `${CDN_BASE_URL}/${sportLower}/jerseys-position/${code}_${pos}.png`;
    }
    case 'team-jerseys':
      return `${CDN_BASE_URL}/${sportLower}/team-jerseys/${code}.png`;
    // Sport-relative: NCAAFB still resolves to `ncaafb/helmet/`, and NFL reaches its own
    // `nfl/helmet/` rather than borrowing college artwork.
    case 'helmet':
      return `${CDN_BASE_URL}/${sportLower}/helmet/${code}.png`;
    case 'teams':
    default: {
      const ext = getFileExtForSport(sportLower);
      return `${CDN_BASE_URL}/${sportLower}/teams/${code}.${ext}`;
    }
  }
}

/**
 * Team-mark URL for matchups / watermarks: contest folder or League IP default.
 */
export function resolveTeamMarkUrl(
  teamCode: string | null | undefined,
  sport: string | null | undefined = 'MLB',
  position?: string | null
): string {
  if (!teamCode || typeof teamCode !== 'string' || !sport) return '';
  const folder = resolveEffectiveTeamAssetFolder(sport);
  return generateTeamAssetUrl(teamCode, sport, folder, position);
}

/**
 * Non-IP / jersey-style player fallback: prefer jersey folders; never use `teams`
 * logos as the player jersey substitute when the contest folder is logos-only.
 */
export function resolveJerseyStyleTeamUrl(
  teamCode: string | null | undefined,
  sport: string | null | undefined = 'MLB',
  position?: string | null
): string {
  if (!teamCode || typeof teamCode !== 'string' || !sport) return '';
  const effective = resolveEffectiveTeamAssetFolder(sport);
  const folder = effective === 'teams' ? defaultTeamAssetFolder(sport, false) : effective;
  return generateTeamAssetUrl(teamCode, sport, folder, position);
}
